import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ActivityCatalogEntriesResponse,
  ActivityCatalogSearchRequest,
  ActivityCatalogSearchResponse,
  ActivityUnitTypeCountsRequest,
  ActivityUnitTypeCountsResponse,
  BootstrapActivityCatalogRequest,
  BootstrapActivityCatalogResponse,
  HideActivityCatalogEntryRequest,
  PromoteActivityCatalogEntryRequest,
} from '../types';

import { useGHGOrganizationId } from './use-ghg-organization-id';

import { createApiClient } from '@/lib/api-client';

const BOOTSTRAP_TIMEOUT_MS = 20000;

interface ActivityCatalogQueryParams {
  scope?: 1 | 2 | 3;
  activityCategory?: string;
  scope3Category?: number;
  unitType?: string;
  includeDiscovered?: boolean;
}

interface ActivitySearchQueryParams {
  scope: 1 | 2 | 3;
  activityCategory: string;
  scope3Category?: number;
  unitType: string;
  query?: string;
  region?: string;
  source?: string;
  year?: number;
  pageSize?: number;
}

interface ActivityUnitTypeCountsQueryParams {
  scope: 1 | 2 | 3;
  activityCategory: string;
  scope3Category?: number;
  query?: string;
  region?: string;
  source?: string;
  year?: number;
}

export const activityCatalogKeys = {
  all: ['activity-catalog'] as const,
  entries: (orgId: string, params: ActivityCatalogQueryParams) =>
    [...activityCatalogKeys.all, 'entries', orgId, params] as const,
  search: (orgId: string, params: ActivitySearchQueryParams) =>
    [...activityCatalogKeys.all, 'search', orgId, params] as const,
  unitTypeCounts: (orgId: string, params: ActivityUnitTypeCountsQueryParams) =>
    [...activityCatalogKeys.all, 'unit-type-counts', orgId, params] as const,
};

function buildEntriesQuery(params: ActivityCatalogQueryParams): string {
  const query = new URLSearchParams();
  if (params.scope !== undefined) {
    query.set('scope', String(params.scope));
  }
  if (params.activityCategory) {
    query.set('activity_category', params.activityCategory);
  }
  if (params.scope3Category !== undefined) {
    query.set('scope3_category', String(params.scope3Category));
  }
  if (params.unitType) {
    query.set('unit_type', params.unitType);
  }
  if (params.includeDiscovered) {
    query.set('include_discovered', 'true');
  }
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

export function useActivityCatalogEntries(
  params: ActivityCatalogQueryParams,
  options?: { enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: activityCatalogKeys.entries(orgId, params),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      const query = buildEntriesQuery(params);
      return apiClient.get<ActivityCatalogEntriesResponse>(
        `/organizations/${orgId}/ghg/activity-catalog/entries${query}`
      );
    },
    enabled: Boolean(orgId) && (options?.enabled ?? true),
  });
}

export function usePromoteActivityCatalogEntry() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PromoteActivityCatalogEntryRequest) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      return apiClient.post(
        `/organizations/${orgId}/ghg/activity-catalog/entries/promote`,
        payload
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityCatalogKeys.all });
    },
  });
}

export function useHideActivityCatalogEntry() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: HideActivityCatalogEntryRequest) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      return apiClient.post(`/organizations/${orgId}/ghg/activity-catalog/entries/hide`, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityCatalogKeys.all });
    },
  });
}

export function useBootstrapActivityCatalog() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BootstrapActivityCatalogRequest) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      const controller = new AbortController();
      const timeout = window.setTimeout(() => {
        controller.abort();
      }, BOOTSTRAP_TIMEOUT_MS);
      try {
        return await apiClient.post<BootstrapActivityCatalogResponse>(
          `/organizations/${orgId}/ghg/activity-catalog/bootstrap`,
          payload,
          { signal: controller.signal }
        );
      } finally {
        window.clearTimeout(timeout);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityCatalogKeys.all });
    },
  });
}

export function useActivitySearchInfinite(
  params: ActivitySearchQueryParams,
  options?: { enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const pageSize = Math.max(1, Math.min(params.pageSize ?? 50, 50));
  const isScope3Search = params.scope === 3;

  const query = useInfiniteQuery({
    queryKey: activityCatalogKeys.search(orgId, {
      ...params,
      pageSize,
    }),
    queryFn: async ({ pageParam, signal }) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      return apiClient.post<ActivityCatalogSearchResponse>(
        `/organizations/${orgId}/ghg/activity-catalog/search`,
        {
          scope: params.scope,
          activityCategory: params.activityCategory,
          ...(params.scope3Category ? { scope3Category: params.scope3Category } : {}),
          unitType: params.unitType,
          ...(params.query ? { query: params.query } : {}),
          ...(params.region ? { region: params.region } : {}),
          ...(params.source ? { source: params.source } : {}),
          ...(params.year ? { year: params.year } : {}),
          page: pageParam,
          pageSize,
        } satisfies ActivityCatalogSearchRequest,
        { signal }
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    enabled:
      Boolean(orgId) &&
      Boolean(params.activityCategory) &&
      Boolean(params.unitType) &&
      (!isScope3Search || typeof params.scope3Category === 'number') &&
      (options?.enabled ?? true),
  });

  return {
    ...query,
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    possibleFilters: query.data?.pages[0]?.possibleFilters,
    hasMore: query.hasNextPage,
  };
}

export function useActivityUnitTypeCounts(
  params: ActivityUnitTypeCountsQueryParams,
  options?: { enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const isScope3Counts = params.scope === 3;

  return useQuery({
    queryKey: activityCatalogKeys.unitTypeCounts(orgId, params),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      return apiClient.post<ActivityUnitTypeCountsResponse>(
        `/organizations/${orgId}/ghg/activity-catalog/unit-type-counts`,
        {
          scope: params.scope,
          activityCategory: params.activityCategory,
          ...(params.scope3Category ? { scope3Category: params.scope3Category } : {}),
          ...(params.query ? { query: params.query } : {}),
          ...(params.region ? { region: params.region } : {}),
          ...(params.source ? { source: params.source } : {}),
          ...(params.year ? { year: params.year } : {}),
        } satisfies ActivityUnitTypeCountsRequest
      );
    },
    enabled:
      Boolean(orgId) &&
      Boolean(params.activityCategory) &&
      (!isScope3Counts || typeof params.scope3Category === 'number') &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}
